/**
 * FiasAIProposal - Change proposal review card (apply/reject).
 *
 * Structural readiness component for arche-specific AI integration.
 * When Fias AI proposes arche state changes, this card lets users review
 * and accept/reject the proposed actions.
 */

import { useState } from 'react';
import { Check, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
interface ArcheProposal {
  proposalId: string;
  actions: Array<{
    actionId: string;
    params: Record<string, unknown>;
    description: string;
  }>;
}

interface FiasAIProposalProps {
  proposal: ArcheProposal;
  onApply: (proposalId: string) => void;
  onReject: (proposalId: string) => void;
  disabled?: boolean;
}

export function FiasAIProposal({ proposal, onApply, onReject, disabled }: FiasAIProposalProps) {
  const [expanded, setExpanded] = useState(false);

  if (disabled) {
    return (
      <div>
        <button
          onClick={() => setExpanded((prev) => !prev)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <Check className="h-3.5 w-3.5 text-green-500" />
          <span>
            {proposal.actions.length} {proposal.actions.length === 1 ? 'change' : 'changes'} applied
          </span>
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
        {expanded && (
          <div className="ml-5 mt-1.5 space-y-1 text-xs text-muted-foreground">
            {proposal.actions.map((action, i) => (
              <div key={i}>{action.description}</div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg p-3 bg-card">
      <div className="text-xs font-medium text-muted-foreground mb-2">Proposed changes</div>
      <div className="space-y-1.5">
        {proposal.actions.map((action, i) => (
          <div key={i} className="text-sm text-foreground">
            {action.description}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Button
          size="sm"
          variant="default"
          className="h-7 text-xs gap-1"
          onClick={() => onApply(proposal.proposalId)}
        >
          <Check className="h-3 w-3" />
          Apply
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1"
          onClick={() => onReject(proposal.proposalId)}
        >
          <X className="h-3 w-3" />
          Reject
        </Button>
      </div>
    </div>
  );
}
